import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/UI/Button';
import { useAuth } from '../context/AuthContext';
import { FileText, Users, Box, BarChart3, ArrowRight, ShieldCheck, Zap, Globe, CheckCircle2 } from 'lucide-react';

export const Landing = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const features = [
    {
      icon: <FileText className="h-7 w-7" />,
      title: 'Facturación Rápida',
      description: 'Crea, envía y administra tus facturas y cotizaciones en segundos con plantillas profesionales.'
    },
    {
      icon: <Users className="h-7 w-7" />,
      title: 'Gestión de Clientes',
      description: 'Mantén un registro ordenado de todos tus clientes, su historial de facturación y proyectos.'
    },
    {
      icon: <Box className="h-7 w-7" />,
      title: 'Control de Inventario',
      description: 'Supervisa tus existencias, movimientos de stock y productos de forma centralizada.'
    },
    {
      icon: <BarChart3 className="h-7 w-7" />,
      title: 'Reportes y Métricas',
      description: 'Toma mejores decisiones con el panel de reportes sobre ventas, ingresos y proyecciones.'
    }
  ];

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-900 text-surface-900 dark:text-gray-100 font-sans selection:bg-primary selection:text-white">
      {/* Navigation - Glassmorphism */}
      <nav className="fixed w-full top-0 z-50 bg-white/80 dark:bg-surface-900/80 backdrop-blur-md border-b border-gray-100 dark:border-surface-800 transition-all">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            <div className="flex items-center gap-2 group cursor-pointer" onClick={() => window.scrollTo(0,0)}>
              <img src="/logo_qi.png" alt="Quinvoice" className="h-9 w-auto transition-transform group-hover:scale-105" />
              <span className="font-extrabold text-2xl tracking-tight text-surface-900 dark:text-white">Quinvoice</span>
            </div>
            <div className="flex gap-4 items-center">
              {isAuthenticated ? (
                <Button onClick={() => navigate('/dashboard')} className="bg-primary hover:bg-primary-hover shadow-lg shadow-primary/30 rounded-xl px-6" rightIcon={<ArrowRight size={18} />}>
                  Ir al Panel
                </Button>
              ) : (
                <>
                  <Link to="/login" className="hidden sm:block">
                    <span className="text-surface-700 dark:text-surface-200 hover:text-primary font-semibold transition-colors px-4 py-2">
                      Iniciar Sesión
                    </span>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-primary hover:bg-primary-hover shadow-lg shadow-primary/30 rounded-xl px-6">
                      Regístrate
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Dark Turquoise Flavor */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-primary dark:bg-surface-900">
        {/* Abstract Background Shapes */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
          <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[70%] rounded-full bg-primary-hover blur-[120px] opacity-70"></div>
          <div className="absolute top-[40%] -right-[10%] w-[40%] h-[60%] rounded-full bg-[#04464C] blur-[100px] opacity-60"></div>
          <div className="absolute bottom-0 left-[20%] w-[60%] h-[40%] rounded-full bg-[#14b8a6] blur-[150px] opacity-20"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-primary-light mb-8 shadow-xl">
              <Zap size={16} className="text-yellow-400" fill="currentColor" />
              <span className="text-sm font-semibold tracking-wide">La mejor plataforma de facturación 2026</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-8 text-white leading-[1.1]">
              Potencia tus <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-200 to-brand-400">Ventas</span> y simplifica tu negocio
            </h1>
            
            <p className="text-xl text-primary-light/90 mb-10 leading-relaxed max-w-2xl mx-auto font-medium">
              Diseñado exclusivamente para freelancers y empresas modernas. Lleva el control absoluto de tus ingresos, clientes e inventario en una plataforma increíblemente rápida.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-5 justify-center items-center">
              <Link to={isAuthenticated ? '/dashboard' : '/register'} className="w-full sm:w-auto">
                <button className="w-full sm:w-auto px-8 py-4 bg-white text-primary hover:bg-surface-50 rounded-2xl font-bold text-lg shadow-[0_0_40px_rgba(255,255,255,0.3)] hover:shadow-[0_0_60px_rgba(255,255,255,0.5)] transition-all flex items-center justify-center gap-2 group">
                  {isAuthenticated ? 'Ir al Dashboard' : 'Comenzar Gratis'}
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
              {!isAuthenticated && (
                <Link to="/login" className="w-full sm:w-auto">
                  <button className="w-full sm:w-auto px-8 py-4 bg-primary-dark/40 hover:bg-primary-dark/60 backdrop-blur-md border border-white/10 text-white rounded-2xl font-bold text-lg transition-all flex items-center justify-center gap-2">
                    Iniciar Sesión
                  </button>
                </Link>
              )}
            </div>
            
            <div className="mt-12 flex items-center justify-center gap-6 text-sm text-primary-light/70 font-medium">
              <span className="flex items-center gap-1.5"><CheckCircle2 size={16} className="text-brand-300"/> Sin tarjeta de crédito</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 size={16} className="text-brand-300"/> Configuración en 1 minuto</span>
            </div>
          </div>
        </div>
        
        {/* Bottom Curve */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0]">
          <svg className="relative block w-[calc(100%+1.3px)] h-[50px] md:h-[100px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" className="fill-surface-50 dark:fill-surface-900"></path>
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-surface-50 dark:bg-surface-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-6 text-surface-900 dark:text-white">Todo para crecer <span className="text-primary">más rápido</span></h2>
            <p className="text-xl text-surface-600 dark:text-surface-400 max-w-2xl mx-auto">
              Herramientas poderosas con un diseño elegante. Automatiza el trabajo aburrido y enfócate en escalar tu negocio.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="p-8 rounded-3xl bg-white dark:bg-surface-800 border border-surface-100 dark:border-surface-700 shadow-card hover:shadow-card-hover hover:-translate-y-2 transition-all duration-300 group relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-110"></div>
                <div className="w-14 h-14 rounded-2xl bg-primary-subtle dark:bg-primary/20 flex items-center justify-center mb-6 text-primary group-hover:scale-110 group-hover:bg-primary group-hover:text-white transition-all duration-300 relative z-10">
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-bold mb-4 text-surface-900 dark:text-white relative z-10">{feature.title}</h3>
                <p className="text-surface-600 dark:text-surface-400 leading-relaxed font-medium relative z-10">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust/Benefits Section */}
      <section className="py-24 bg-white dark:bg-surface-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-primary rounded-[3rem] p-10 md:p-20 text-white text-center shadow-[0_20px_50px_rgba(8,112,122,0.3)] relative overflow-hidden">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent bg-[length:20px_20px]"></div>
            <div className="absolute top-0 right-0 -mr-20 -mt-20 opacity-10 text-white">
              <ShieldCheck size={350} />
            </div>
            <div className="absolute bottom-0 left-0 -ml-20 -mb-20 opacity-10 text-white">
              <Globe size={300} />
            </div>
            
            <h2 className="text-4xl md:text-5xl font-extrabold mb-6 relative z-10 tracking-tight">Seguridad que <span className="text-brand-300">Inspira Confianza</span></h2>
            <p className="text-xl text-primary-light mb-16 max-w-3xl mx-auto relative z-10 leading-relaxed">
              Tus registros financieros, catálogo de clientes e inventario están protegidos con encriptación de nivel bancario. Tu negocio siempre seguro y disponible en la nube.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative z-10">
              <div className="flex flex-col items-center p-6 rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 transition-colors">
                <ShieldCheck className="h-12 w-12 mb-5 text-brand-300" />
                <span className="font-bold text-xl">Backups Diarios</span>
                <p className="text-primary-light text-sm mt-3">Nunca perderás un solo dato importante de tu empresa.</p>
              </div>
              <div className="flex flex-col items-center p-6 rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 transition-colors">
                <Globe className="h-12 w-12 mb-5 text-brand-300" />
                <span className="font-bold text-xl">Acceso Global</span>
                <p className="text-primary-light text-sm mt-3">Factura desde cualquier dispositivo y en cualquier lugar.</p>
              </div>
              <div className="flex flex-col items-center p-6 rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 transition-colors">
                <Zap className="h-12 w-12 mb-5 text-brand-300" />
                <span className="font-bold text-xl">99.9% Uptime</span>
                <p className="text-primary-light text-sm mt-3">Infraestructura robusta que crece junto a tu volumen de ventas.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-surface-50 dark:bg-surface-900 pt-16 pb-8 border-t border-surface-200 dark:border-surface-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-8">
            <div className="flex items-center gap-3">
              <img src="/logo_qi.png" alt="Quinvoice" className="h-8 w-auto" />
              <span className="font-extrabold text-xl tracking-tight text-surface-900 dark:text-white">Quinvoice</span>
            </div>
            <div className="flex gap-6 text-surface-500 font-medium">
              <Link to="/terms" className="hover:text-primary transition-colors">Términos</Link>
              <Link to="/privacy" className="hover:text-primary transition-colors">Privacidad</Link>
              <Link to="/support" className="hover:text-primary transition-colors">Soporte</Link>
            </div>
          </div>
          <div className="text-center text-surface-400 dark:text-surface-600 text-sm font-medium pt-8 border-t border-surface-200 dark:border-surface-800">
            Quinvoice © {new Date().getFullYear()}. La plataforma definitiva para tu negocio.
          </div>
        </div>
      </footer>
    </div>
  );
};
